import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import toast from 'react-hot-toast';
import { FirebaseGoogleSignIn } from '../../../../components/googleSingIn/googleSingIn';
import { InAppBrowserBanner } from '../../../../components/InAppBrowserBanner/InAppBrowserBanner';
import { isInAppBrowser } from '../../../../shared/lib/browser/in-app-browser';
import styles from './styles.module.css';
import Button from '../../../../shared/ui/button/button';

import { singInRequest } from '../../api/index';
import { useLocale } from '../../../../context/LocaleContext';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../../../../context/AuthContext';
import {
	formatErrorDetail,
	isNetworkError,
} from '../../../../shared/api/apiError';

export const EmailVerified = () => {
	const intl = useIntl();
	const { setErrorModalShow } = useLocale();
	const navigate = useNavigate();
	const { onLogin } = useAuth();
	const [icon, setIcon] = useState(faEyeSlash);
	const [type, setType] = useState('password');
	const [isToastOpen, setIsToastOpen] = useState(false);
	const [errorMessage, seterrorMessage] = useState([]);
	const [errors, setErrors] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState({
		email: '',
		password: '',
		rememberMe: false,
	});
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

	const validateField = (name, value) => {
		switch (name) {
			case 'email':
				if (!value.trim())
					return intl.formatMessage({ id: 'EmailVerifiedValidationId1' });
				if (!emailRegex.test(value))
					return intl.formatMessage({ id: 'EmailVerifiedValidationId2' });
				return '';
			case 'password':
				if (!value.trim())
					return intl.formatMessage({ id: 'EmailVerifiedValidationId3' });
				return '';

			default:
				return '';
		}
	};
	const validateAllFields = () => {
		const newErrors = {};

		Object.keys(formData).forEach(key => {
			const error = validateField(key, formData[key]);
			if (error) {
				newErrors[key] = error;
			}
		});

		return newErrors;
	};
	const handleToggle = () => {
		if (type === 'password') {
			setIcon(faEye);
			setType('text');
		} else {
			setIcon(faEyeSlash);
			setType('password');
		}
	};
	const handleInputChange = e => {
		const { name, value, type, checked } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
		if (errors[name]) {
			setErrors(prev => ({
				...prev,
				[name]: '',
			}));
		}
	};

	const handleSubmit = async e => {
		e.preventDefault();
		setIsLoading(true);

		const userData = {
			email: formData.email.trim(),
			password: formData.password,
			rememberMe: true,
		};

		const validationErrors = validateAllFields();

		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			setIsLoading(false);
			return;
		}

		try {
			const sigIn = await singInRequest(userData);

			// Server unreachable / network error – axios error without a response.
			// Without this the call fell through every branch below and left the
			// form silently idle, showing the user nothing at all.
			if (isNetworkError(sigIn)) {
				seterrorMessage(
					intl.formatMessage({ id: 'EmailVerifiedValidationId6' }),
				);
				setIsLoading(false);
				return;
			}

			// Check if response is an error
			if (sigIn && sigIn.response) {
				seterrorMessage(
					formatErrorDetail(
						sigIn.response?.data?.detail,
						intl.formatMessage({ id: 'EmailVerifiedValidationId4' }),
					),
				);
				setIsLoading(false);
				return;
			}

			// Check if MFA is required
			if (sigIn && sigIn.requires_mfa) {
				// MFA handling would go here if needed
				// For now, show error as this component doesn't have MFA UI
				seterrorMessage(
					intl.formatMessage({ id: 'EmailVerifiedValidationId5' }),
				);
				setIsLoading(false);
				return;
			}

			// Normal login success
			if (sigIn && sigIn.access_token) {
				window.localStorage.setItem('accessToken', sigIn.access_token);
				window.localStorage.setItem('authProvider', 'email');
				await onLogin(userData);
				navigate('/overview');
			}
		} catch (err) {
			console.error(err);
			seterrorMessage(intl.formatMessage({ id: 'EmailVerifiedValidationId6' }));
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className={styles.container}>
			<div className={styles.card}>
				<div className={styles.header}>
					<div className={styles.successIcon}>
						<svg
							width='24'
							height='24'
							viewBox='0 0 24 24'
							fill='none'
							xmlns='http://www.w3.org/2000/svg'
						>
							<path
								d='M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z'
								stroke='currentColor'
								strokeWidth='2'
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
						</svg>
					</div>
					<h1 className={styles.title}>
						{intl.formatMessage({ id: 'EmailVerifiedId1' })}
					</h1>
					<p className={styles.subtitle}>
						{intl.formatMessage({ id: 'EmailVerifiedId2' })}
					</p>
				</div>

				{/* <form onSubmit={handleSubmit}> */}
				<div className={styles.form}>
					<div className={styles.inputGroup}>
						<label htmlFor='email' className={styles.label}>
							{intl.formatMessage({ id: 'EmailVerifiedId3' })}
						</label>
						<input
							type='email'
							id='email'
							name='email'
							value={formData.email}
							onChange={handleInputChange}
							className={styles.input}
							placeholder={intl.formatMessage({ id: 'EmailVerifiedId4' })}
							required
						/>
						{errors.email && (
							<span className={styles.errorText}>{errors.email}</span>
						)}
					</div>

					<label htmlFor='password' className={styles.label}>
						{intl.formatMessage({ id: 'EmailVerifiedId5' })}
					</label>
					<div className={styles.inputGroup}>
						<input
							type={type}
							id='password'
							name='password'
							value={formData.password}
							onChange={handleInputChange}
							className={styles.input}
							placeholder={intl.formatMessage({ id: 'EmailVerifiedId6' })}
							required
						/>
						<span className={styles.eyeBox} onClick={handleToggle}>
							<FontAwesomeIcon icon={icon} />
						</span>
					</div>
					{errors.password && (
						<span className={styles.errorText}>{errors.password}</span>
					)}
					{errorMessage ? (
						<span className={styles.errorText}>{errorMessage}</span>
					) : (
						''
					)}

					<Link to='/forgot-password' className={styles.forgotLink}>
						{intl.formatMessage({ id: 'EmailVerifiedId7' })}
					</Link>

					<Button type='submit' fullWidth onClick={handleSubmit}>
						{intl.formatMessage({ id: 'EmailVerifiedId8' })}
					</Button>
					{useMemo(() => isInAppBrowser(), []) && <InAppBrowserBanner />}
					<div className={styles.linelBlock}>
						<div></div>
						<p>{intl.formatMessage({ id: 'EmailVerifiedId9' })}</p>
						<div></div>
					</div>

					<FirebaseGoogleSignIn
						isInAppBrowser={useMemo(() => isInAppBrowser(), [])}
						onInAppBrowserGoogleClick={() =>
							toast(
								intl.formatMessage({ id: 'InAppBrowserBannerToastMessage' }),
								{
									icon: '⚠️',
									duration: 5000,
								},
							)
						}
					/>
					{/* </form> */}
				</div>
				<div className={styles.footer}>
					<p className={styles.footerText}>
						{intl.formatMessage({ id: 'EmailVerifiedId10' })}
						<Link to='/sign-up' className={styles.link}>
							{intl.formatMessage({ id: 'EmailVerifiedId11' })}
						</Link>
					</p>
				</div>
			</div>
		</div>
	);
};
